"use client";

// src/alert.tsx
import * as React4 from "react";
import {
  systemForwardRef as systemForwardRef3,
  useMergeStyles,
  useStyleConfig as useStyleConfig3
} from "@stepstone-genesis/system";
import {
  MediaBody as MediaBody2,
  MediaObject as MediaObject2,
  MediaFigure as MediaFigure2
} from "@stepstone-genesis/components-media-object-layout";
import { RemoveIcon } from "@stepstone-genesis/icons";
import { Text as Text2 } from "@stepstone-genesis/components-text";
import { Button } from "@stepstone-genesis/components-button";

// src/context.tsx
import * as React from "react";
import {
  CircleCheckIcon,
  CircleXIcon,
  WarningIcon,
  LightbulbIcon,
  InfoIcon
} from "@stepstone-genesis/icons";
var variants = {
  default: { role: "status", icon: InfoIcon },
  info: { role: "status", icon: LightbulbIcon },
  warning: { role: "alert", icon: WarningIcon },
  positive: { role: "alert", icon: CircleCheckIcon },
  negative: { role: "alert", icon: CircleXIcon }
};
var AlertContext = React.createContext(null);
var useAlertContext = () => {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error("useAlertContext must be used within AlertContextProvider");
  }
  return context;
};

// src/primitive.tsx
import * as React3 from "react";
import {
  systemForwardRef as systemForwardRef2,
  Base,
  useStyleConfig as useStyleConfig2,
  mergeStyles,
  useTheme
} from "@stepstone-genesis/system";
import { getValidChildren } from "@stepstone-genesis/utilities";

// src/styles.ts
var root = ({
  theme,
  hasBorder
}) => {
  var _a, _b;
  return {
    outline: hasBorder ? "1px solid" : "none",
    outlineOffset: hasBorder ? "-1px" : "none",
    borderRadius: "componentAlertRadius",
    borderLeft: "none",
    position: "relative",
    boxShadow: hasBorder ? "boxShadowNone" : "componentAlertBoxShadow",
    overflow: "hidden",
    _before: {
      display: (_a = theme.shadows) == null ? void 0 : _a.componentAlertDisplayBefore,
      content: "''",
      position: "absolute",
      width: `calc(${(_b = theme.sizes) == null ? void 0 : _b.size10} / 2)`,
      height: "100%",
      left: 0,
      top: 0,
      borderTopLeftRadius: "radiusM",
      borderBottomLeftRadius: "radiusM"
    }
  };
};
var container = {
  default: {
    backgroundColor: "colorStatusDefaultBg",
    outlineColor: "componentAlertDefaultBorderColor",
    _before: {
      content: "''",
      backgroundColor: "colorStatusDefaultFg"
    }
  },
  positive: {
    backgroundColor: "colorStatusPositiveBg",
    outlineColor: "componentAlertPositiveBorderColor",
    _before: {
      content: "''",
      backgroundColor: "colorStatusPositiveFg"
    }
  },
  warning: {
    backgroundColor: "colorStatusWarningBg",
    outlineColor: "componentAlertWarningBorderColor",
    _before: {
      content: "''",
      backgroundColor: "colorStatusWarningFg"
    }
  },
  negative: {
    backgroundColor: "colorStatusNegativeBg",
    outlineColor: "componentAlertNegativeBorderColor",
    _before: {
      content: "''",
      backgroundColor: "colorStatusNegativeFg"
    }
  },
  info: {
    backgroundColor: "colorStatusInfoBg",
    outlineColor: "componentAlertInfoBorderColor",
    _before: {
      content: "''",
      backgroundColor: "colorStatusInfoFg"
    }
  }
};
var dismissButton = {
  minWidth: "auto",
  minHeight: "auto",
  width: "size30",
  height: "size30"
};
var iconStyle = {
  default: {
    color: "colorStatusDefaultFg"
  },
  positive: {
    color: "colorStatusPositiveFg"
  },
  warning: {
    color: "colorStatusWarningFg"
  },
  negative: {
    color: "colorStatusNegativeFg"
  },
  info: {
    color: "colorStatusInfoFg"
  }
};
var styles = {
  root,
  container,
  iconStyle,
  dismissButton
};

// src/definition.ts
var THEME_DEFINITION_PREFIX = "ALERT";
var THEME_DEFINITION_ELEMENTS = {
  ALERT: `${THEME_DEFINITION_PREFIX}`,
  DISMISS: `${THEME_DEFINITION_PREFIX}_DISMISS`,
  ICON: `${THEME_DEFINITION_PREFIX}_ICON`,
  CONTENT: `${THEME_DEFINITION_PREFIX}_CONTENT`,
  CONTAINER: `${THEME_DEFINITION_PREFIX}_CONTAINER`,
  HEADER: `${THEME_DEFINITION_PREFIX}_HEADER`
};

// src/header.tsx
import * as React2 from "react";
import { systemForwardRef, useStyleConfig } from "@stepstone-genesis/system";
import {
  MediaBody,
  MediaObject,
  MediaFigure
} from "@stepstone-genesis/components-media-object-layout";
import { Text } from "@stepstone-genesis/components-text";
var AlertHeader = systemForwardRef(
  function AlertHeader2({ children, ...props }, ref) {
    const {
      config: { icon: Icon },
      customIcon: CustomIcon,
      isIconVisible = true,
      isDismissible
    } = useAlertContext();
    const { ALERT_DISMISS } = useStyleConfig("ALERT_DISMISS", {});
    return /* @__PURE__ */ React2.createElement(
      MediaObject,
      {
        sx: { px: "spaceM", py: "componentAlertPadding", ...props.sx },
        element: THEME_DEFINITION_ELEMENTS.HEADER
      },
      (Boolean(Icon) || Boolean(CustomIcon)) && isIconVisible && /* @__PURE__ */ React2.createElement(MediaFigure, { alignment: "start", spacing: "spaceS" }, /* @__PURE__ */ React2.createElement(AlertIcon, { isDecorative: false })),
      /* @__PURE__ */ React2.createElement(MediaBody, null, /* @__PURE__ */ React2.createElement(
        Text,
        {
          as: "h4",
          ...props,
          color: "colorTextPrimaryDefault",
          fontWeight: "fontWeightMedium",
          fontSize: "fontSizeM",
          ref
        },
        children
      )),
      isDismissible && /* @__PURE__ */ React2.createElement(MediaFigure, { alignment: "end", spacing: "spaceNone" }, /* @__PURE__ */ React2.createElement(
        AlertDismissButton,
        {
          a11yLabel: "dismiss",
          sx: ALERT_DISMISS.styles,
          element: THEME_DEFINITION_ELEMENTS.DISMISS
        }
      ))
    );
  }
);

// src/primitive.tsx
var AlertPrimitive = systemForwardRef2(
  function AlertPrimitive2(props, ref) {
    const {
      children,
      isDismissible,
      role,
      variant = "info",
      onDismiss,
      customIcon,
      hasBorder = true,
      sx,
      ...rest
    } = props;
    const config = React3.useMemo(() => variants[variant], [variant]);
    const validChildren = getValidChildren(children);
    const { theme } = useTheme();
    const { ALERT } = useStyleConfig2("ALERT", {
      styles: {
        base: styles.root({ theme, hasBorder }),
        variants: styles.container
      },
      props: {
        variant
      }
    });
    const { ALERT_CONTAINER } = useStyleConfig2("ALERT_CONTAINER", {
      props: () => ({
        variant: "compact",
        px: "spaceM",
        py: "componentAlertPadding",
        backgroundColor: rest.title ? "colorWhite" : "transparent"
      })
    });
    return /* @__PURE__ */ React3.createElement(
      AlertContext.Provider,
      {
        value: {
          config,
          variant,
          isDismissible,
          role,
          onDismiss,
          customIcon,
          ...rest
        }
      },
      /* @__PURE__ */ React3.createElement(
        Base,
        {
          as: "div",
          role: role || config.role,
          sx: mergeStyles([ALERT.styles, sx]),
          ref,
          element: THEME_DEFINITION_ELEMENTS.ALERT,
          ...rest
        },
        rest.title && /* @__PURE__ */ React3.createElement(AlertHeader, null, rest.title),
        /* @__PURE__ */ React3.createElement(
          Base,
          {
            element: THEME_DEFINITION_ELEMENTS.CONTAINER,
            ...ALERT_CONTAINER.props,
            sx: {
              px: "spaceM",
              pt: "componentAlertPadding",
              pb: rest.title ? "spaceM" : "componentAlertPadding"
              // To ensure the bottom part of the content has the correct padding in both versions
            }
          },
          /* @__PURE__ */ React3.createElement(Base, { element: THEME_DEFINITION_ELEMENTS.CONTENT }, validChildren)
        )
      )
    );
  }
);

// src/alert.tsx
var AlertContent = systemForwardRef3(
  function AlertContent2({ children, size = "medium", ...props }, ref) {
    return /* @__PURE__ */ React4.createElement(Text2, { size, ...props, ref }, children);
  }
);
var AlertIcon = systemForwardRef3(
  function AlertIcon2(props, ref) {
    const {
      config: { icon: Icon },
      customIcon: CustomIcon,
      isIconVisible = true,
      variant
    } = useAlertContext();
    const { ALERT_ICON } = useStyleConfig3("ALERT_ICON", {
      styles: {
        variants: styles.iconStyle
      },
      props: {
        variant
      }
    });
    if (!(Boolean(Icon) || Boolean(CustomIcon)) || !isIconVisible) return null;
    const RenderAlertIcon = CustomIcon != null ? CustomIcon : Icon;
    return RenderAlertIcon ? /* @__PURE__ */ React4.createElement(
      RenderAlertIcon,
      {
        sx: ALERT_ICON.styles,
        element: THEME_DEFINITION_ELEMENTS.ICON,
        size: "large",
        ...props,
        ref
      }
    ) : null;
  }
);
var AlertDismissButton = systemForwardRef3(function AlertDismissButton2({
  children,
  isDismissible,
  onDismiss,
  a11yLabel = "dismiss alert",
  sx,
  ...props
}, ref) {
  var _a, _b;
  const ctx = useAlertContext();
  const buttonStyles = useMergeStyles(
    [styles.dismissButton, sx ? sx : {}],
    [sx]
  );
  return ((_a = ctx.isDismissible) != null ? _a : isDismissible) ? /* @__PURE__ */ React4.createElement(
    Button,
    {
      a11yLabel,
      variant: "linkSecondary",
      ref,
      onClick: (_b = ctx.onDismiss) != null ? _b : onDismiss,
      sx: buttonStyles,
      ...props,
      ...ctx.dismissButtonProps
    },
    children != null ? children : /* @__PURE__ */ React4.createElement(RemoveIcon, { size: "large" })
  ) : null;
});
var AlertLayout = ({
  children,
  title
}) => {
  const {
    config: { icon: Icon },
    customIcon: CustomIcon,
    isIconVisible = true,
    isDismissible
  } = useAlertContext();
  const { ALERT_DISMISS } = useStyleConfig3("ALERT_DISMISS", {});
  return title ? /* @__PURE__ */ React4.createElement(AlertContent, null, children) : /* @__PURE__ */ React4.createElement(MediaObject2, null, (Boolean(Icon) || Boolean(CustomIcon)) && isIconVisible && /* @__PURE__ */ React4.createElement(MediaFigure2, { alignment: "start", spacing: "spaceS" }, /* @__PURE__ */ React4.createElement(AlertIcon, { isDecorative: false })), /* @__PURE__ */ React4.createElement(MediaBody2, null, /* @__PURE__ */ React4.createElement(AlertContent, null, children)), isDismissible && /* @__PURE__ */ React4.createElement(MediaFigure2, { alignment: "end", spacing: "spaceNone" }, /* @__PURE__ */ React4.createElement(
    AlertDismissButton,
    {
      a11yLabel: "dismiss",
      sx: ALERT_DISMISS.styles,
      element: THEME_DEFINITION_ELEMENTS.DISMISS
    }
  )));
};
var Alert = systemForwardRef3(function Alert2({ children, ...props }, ref) {
  return /* @__PURE__ */ React4.createElement(AlertPrimitive, { ...props, ref }, /* @__PURE__ */ React4.createElement(AlertLayout, { title: props.title }, children));
});
export {
  Alert,
  AlertContent,
  AlertDismissButton,
  AlertHeader,
  AlertIcon,
  AlertPrimitive
};
